import { useEffect, useRef, useState } from "react";
import { Plus, Loader2, Trash2, FlaskConical, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PANELS = {
    "CBC": [
        ["Hemoglobin", "g/dL", "12-15.5"], ["TLC (WBC)", "x10³/µL", "4-11"], ["Platelets", "lakh/µL", "1.5-4.5"],
        ["RBC Count", "mill/µL", "4.2-5.4"], ["PCV (Hct)", "%", "36-46"], ["MCV", "fL", "83-101"],
        ["MCH", "pg", "27-32"], ["MCHC", "g/dL", "31.5-34.5"], ["Neutrophils", "%", "40-70"],
        ["Lymphocytes", "%", "20-40"], ["ESR", "mm/hr", "0-20"],
    ],
    "Blood Sugar": [
        ["FBS (Fasting)", "mg/dL", "70-100"], ["PPBS (Post-meal)", "mg/dL", "70-140"],
        ["RBS (Random)", "mg/dL", "70-140"], ["HbA1c", "%", "4-5.6"],
    ],
    "Lipid Profile": [
        ["Total Cholesterol", "mg/dL", "0-200"], ["Triglycerides", "mg/dL", "0-150"],
        ["HDL", "mg/dL", "40-60"], ["LDL", "mg/dL", "0-100"], ["VLDL", "mg/dL", "10-40"],
    ],
    "LFT": [
        ["Bilirubin Total", "mg/dL", "0.3-1.2"], ["SGOT (AST)", "U/L", "5-40"], ["SGPT (ALT)", "U/L", "7-56"],
        ["Alk. Phosphatase", "U/L", "44-147"], ["Total Protein", "g/dL", "6.4-8.3"], ["Albumin", "g/dL", "3.5-5.5"],
    ],
    "KFT / RFT": [
        ["Blood Urea", "mg/dL", "17-43"], ["Serum Creatinine", "mg/dL", "0.7-1.3"], ["Uric Acid", "mg/dL", "3.5-7.2"],
        ["Sodium", "mEq/L", "136-145"], ["Potassium", "mEq/L", "3.5-5.1"], ["Chloride", "mEq/L", "98-107"],
    ],
    "Thyroid": [["TSH", "µIU/mL", "0.4-4.0"], ["T3", "ng/mL", "0.8-2.0"], ["T4", "µg/dL", "5.1-14.1"]],
    "ANC Profile": [
        ["Hemoglobin", "g/dL", "11-14"], ["FBS", "mg/dL", "70-92"], ["TSH", "µIU/mL", "0.1-2.5"],
        ["HIV", "", "Non-reactive"], ["HBsAg", "", "Negative"], ["VDRL", "", "Non-reactive"], ["Urine Sugar", "", "Nil"],
    ],
    "Urine Routine": [
        ["pH", "", "4.6-8"], ["Specific Gravity", "", "1.005-1.030"], ["Protein", "", "Nil"],
        ["Sugar", "", "Nil"], ["Pus Cells", "/hpf", "0-5"], ["RBC", "/hpf", "0-2"],
    ],
};

function flag(value, range) {
    const v = parseFloat(value);
    if (isNaN(v)) return "";
    const m = range.match(/^([\d.]+)\s*-\s*([\d.]+)$/);
    if (!m) return "";
    if (v < parseFloat(m[1])) return "L";
    if (v > parseFloat(m[2])) return "H";
    return "";
}

export default function LabReports({ patientId }) {
    const [reports, setReports] = useState(null);
    const [adding, setAdding] = useState(false);
    const [panel, setPanel] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [values, setValues] = useState({});
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const onUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post(`/patients/${patientId}/reports/upload`, fd);
            if (data.results?.length) toast.success(`Auto-extracted ${data.results.length} values from ${f.name}`);
            else toast.warning(`Uploaded ${f.name} — no values auto-extracted, add manually`);
            load();
        } catch (err) { toast.error(fmtErr(err)); } finally { setUploading(false); e.target.value = ""; }
    };

    const viewFile = async (r) => {
        try {
            const res = await api.get(`/reports/${r.id}/file`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: r.content_type || "application/pdf" }));
            window.open(url, "_blank");
        } catch (err) { toast.error(fmtErr(err)); }
    };

    const load = async () => {
        try {
            const { data } = await api.get(`/patients/${patientId}/reports`);
            setReports(data);
        } catch (e) { toast.error(fmtErr(e)); }
    };

    useEffect(() => { load(); }, [patientId]);

    const save = async () => {
        const results = PANELS[panel]
            .filter(([t]) => (values[t] || "").trim())
            .map(([t, u, r]) => ({ test: t, value: values[t].trim(), unit: u, range: r, flag: flag(values[t], r) }));
        if (results.length === 0) { toast.error("Enter at least one value"); return; }
        setSaving(true);
        try {
            await api.post(`/patients/${patientId}/reports`, { panel, date, results, notes });
            toast.success(`${panel} report saved`);
            setAdding(false);
            setPanel("");
            setValues({});
            setNotes("");
            load();
        } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
    };

    const remove = async (rid) => {
        if (!window.confirm("Delete this report?")) return;
        try {
            await api.delete(`/patients/${patientId}/reports/${rid}`);
            toast.success("Report deleted");
            load();
        } catch (e) { toast.error(fmtErr(e)); }
    };

    return (
        <div className="space-y-4" data-testid="lab-reports-section">
            <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><FlaskConical className="h-4 w-4 text-[#E06D1F]" /> Lab / Blood Reports</h3>
                <div className="flex gap-2">
                    <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" data-testid="report-file-input" onChange={onUpload} />
                    <Button data-testid="upload-report-btn" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1.5">
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? "Extracting..." : "Upload & Auto-Extract"}
                    </Button>
                    <Button data-testid="add-report-btn" size="sm" onClick={() => setAdding(!adding)} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5"><Plus className="h-4 w-4" /> Add Report</Button>
                </div>
            </div>

            {adding && (
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4 animate-fade-up" data-testid="add-report-form">
                    <div className="flex flex-wrap gap-1.5" data-testid="report-panel-tabs">
                        {Object.keys(PANELS).map((p) => (
                            <button key={p} type="button" data-testid={`panel-tab-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => { setPanel(p); setValues({}); }} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 ${panel === p ? "bg-[#E06D1F] text-white border-[#E06D1F]" : "bg-white text-slate-600 border-slate-300 hover:border-orange-300"}`}>{p}</button>
                        ))}
                    </div>
                    {panel && (
                        <>
                            <div className="max-w-xs space-y-1.5"><Label>Report Date</Label><Input data-testid="report-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {PANELS[panel].map(([t, u, r]) => (
                                    <div key={t} className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
                                        <p className="text-xs font-semibold text-slate-800">{t}</p>
                                        <p className="text-[10px] text-slate-400">Normal: {r}{u ? ` ${u}` : ""}</p>
                                        <input data-testid={`report-value-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} value={values[t] || ""} onChange={(e) => setValues({ ...values, [t]: e.target.value })} placeholder="Enter value" className="mt-1 w-full bg-transparent text-sm font-semibold text-slate-900 outline-none placeholder:text-slate-300 placeholder:font-normal" />
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-1.5"><Label>Notes</Label><Input data-testid="report-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Lab name, remarks..." /></div>
                            <Button data-testid="report-save-btn" onClick={save} disabled={saving} className="bg-[#E06D1F] hover:bg-[#B45309] text-white">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Report</Button>
                        </>
                    )}
                </div>
            )}

            {!reports ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#E06D1F]" /></div>
            ) : reports.length === 0 ? (
                <p className="text-sm text-slate-500 py-4" data-testid="no-reports">No lab reports yet.</p>
            ) : (
                <div className="space-y-3">
                    {reports.map((r) => (
                        <div key={r.id} data-testid={`report-card-${r.id}`} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                                <p className="text-sm font-semibold text-slate-900">{r.panel} <span className="font-normal text-slate-400">· {r.date}</span>{r.original_filename ? <span className="ml-2 text-xs font-normal text-slate-400">({r.original_filename})</span> : ""}</p>
                                <div className="flex items-center gap-1">
                                    {r.file_path && <Button data-testid={`report-file-${r.id}`} size="sm" variant="ghost" onClick={() => viewFile(r)} className="text-slate-500 hover:text-[#E06D1F] gap-1"><FileText className="h-4 w-4" /> File</Button>}
                                    <Button data-testid={`report-delete-${r.id}`} size="sm" variant="ghost" onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                            <div className="px-5 py-3">
                                <table className="w-full text-sm">
                                    <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
                                        <th className="py-1 pr-3">Test</th><th className="py-1 pr-3">Value</th><th className="py-1 pr-3">Unit</th><th className="py-1">Normal Range</th>
                                    </tr></thead>
                                    <tbody>
                                        {r.results.map((res, i) => (
                                            <tr key={i} className="border-t border-slate-50">
                                                <td className="py-1.5 pr-3 text-slate-700">{res.test}</td>
                                                <td className="py-1.5 pr-3 font-semibold text-slate-900">
                                                    {res.value}
                                                    {res.flag === "H" && <span className="ml-1.5 rounded bg-red-50 border border-red-200 px-1 text-[10px] font-bold text-red-600">H</span>}
                                                    {res.flag === "L" && <span className="ml-1.5 rounded bg-blue-50 border border-blue-200 px-1 text-[10px] font-bold text-blue-600">L</span>}
                                                </td>
                                                <td className="py-1.5 pr-3 text-slate-500 text-xs">{res.unit}</td>
                                                <td className="py-1.5 text-slate-500 text-xs">{res.range}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {r.notes && <p className="mt-2 text-xs text-slate-500">Notes: {r.notes}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
